import { AlertTriangle } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { Notification } from '../types/notification.types';

interface DangerNotificationDialogProps {
  notification: Notification;
  open: boolean;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
}

export const DangerNotificationDialog: React.FC<
  DangerNotificationDialogProps
> = ({ notification, open, onClose, onMarkAsRead }) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  // Set up avatar URL when notification changes
  useEffect(() => {
    if (notification?.action_user_id?.avatar) {
      const baseUrl = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER || '';

      // Check if the avatar path is valid

      const url = `${baseUrl}/public/avatar/thumb/${notification.action_user_id.avatar}`;

      // Try to fetch the image to check if it exists
      fetch(url, { method: 'HEAD' })
        .then((response) => {
          if (response.ok) {
            setAvatarUrl(url);
            setAvatarError(false);
          } else {
            console.error('Avatar image not found:', url);
            setAvatarError(true);
          }
        })
        .catch((error) => {
          console.error('Error checking avatar URL:', error);
          setAvatarError(true);
        });
    } else {
      setAvatarUrl(null);
    }
  }, [notification]);

  const handleMarkAsRead = () => {
    onMarkAsRead(notification._id);
    onClose();
  };

  // Get user initials for avatar fallback
  const getUserInitials = useCallback(() => {
    if (!notification.action_user_id?.empName) return '??';

    const nameParts = notification.action_user_id.empName.split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

    return (
      nameParts[0].charAt(0).toUpperCase() +
      nameParts[nameParts.length - 1].charAt(0).toUpperCase()
    );
  }, [notification.action_user_id?.empName]);

  // Handle avatar load error
  const handleAvatarError = () => {
    console.error('Avatar failed to load:', avatarUrl);
    setAvatarError(true);
  };

  // Enhanced markdown rendering
  const renderMarkdown = () => {
    if (!notification.message) return null;

    // Split message into lines for processing
    const lines = notification.message.split('\n');
    const elements: React.ReactNode[] = [];

    let inList = false;
    let listItems: React.ReactNode[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Empty line - add spacing
      if (!line) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(<div key={`space-${i}`} className="h-2" />);
        continue;
      }

      // Handle horizontal rule
      if (line === '---' || line === '***' || line === '___') {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <hr
            key={`hr-${i}`}
            className="my-3 border-t border-red-200 dark:border-red-800"
          />,
        );
        continue;
      }

      // Handle headings
      if (line.startsWith('# ')) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <h1
            key={i}
            className="text-lg font-bold text-red-700 dark:text-red-400"
          >
            {processInlineMarkdown(line.substring(2))}
          </h1>,
        );
        continue;
      }

      if (line.startsWith('## ')) {
        if (inList) {
          elements.push(
            <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
              {listItems}
            </ul>,
          );
          inList = false;
          listItems = [];
        }
        elements.push(
          <h2
            key={i}
            className="text-base font-semibold text-red-700 dark:text-red-400"
          >
            {processInlineMarkdown(line.substring(3))}
          </h2>,
        );
        continue;
      }

      // Handle list items
      if (line.startsWith('- ') || line.startsWith('* ')) {
        inList = true;
        const content = line.substring(2);
        listItems.push(
          <li key={`item-${i}`} className="flex items-start gap-2">
            <span className="mt-1 text-red-500">•</span>
            <span className="flex-1">{processInlineMarkdown(content)}</span>
          </li>,
        );
        continue;
      }

      // Handle numbered list items
      const numberedListMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedListMatch) {
        inList = true;
        const [, number, content] = numberedListMatch;
        listItems.push(
          <li key={`item-${i}`} className="flex items-start gap-2">
            <span className="mt-0.5 min-w-5 text-red-500">{number}.</span>
            <span className="flex-1">{processInlineMarkdown(content)}</span>
          </li>,
        );
        continue;
      }

      // Handle regular paragraphs
      if (inList) {
        elements.push(
          <ul key={`list-${i}`} className="my-2 space-y-1 text-sm">
            {listItems}
          </ul>,
        );
        inList = false;
        listItems = [];
      }

      elements.push(
        <p key={i} className="text-sm">
          {processInlineMarkdown(line)}
        </p>,
      );
    }

    // Add any remaining list items
    if (inList && listItems.length > 0) {
      elements.push(
        <ul key="final-list" className="my-2 space-y-1 text-sm">
          {listItems}
        </ul>,
      );
    }

    return <div className="space-y-2">{elements}</div>;
  };

  // Process inline markdown (bold, italic, code)
  const processInlineMarkdown = (text: string): React.ReactNode => {
    if (!text) return null;

    const parts: React.ReactNode[] = [];
    let currentText = '';
    let i = 0;

    while (i < text.length) {
      // Bold text with **
      if (text.substring(i, i + 2) === '**' && i + 2 < text.length) {
        // Add current text if any
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        // Find closing **
        const startBold = i + 2;
        let endBold = text.indexOf('**', startBold);
        if (endBold === -1) endBold = text.length;

        const boldText = text.substring(startBold, endBold);
        parts.push(
          <strong
            key={`bold-${i}`}
            className="font-semibold text-red-800 dark:text-red-300"
          >
            {boldText}
          </strong>,
        );

        i = endBold + 2;
        continue;
      }

      // Italic text with *
      if (text[i] === '*' && text[i + 1] !== '*' && i + 1 < text.length) {
        // Add current text if any
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        // Find closing *
        const startItalic = i + 1;
        let endItalic = text.indexOf('*', startItalic);
        if (endItalic === -1) endItalic = text.length;

        const italicText = text.substring(startItalic, endItalic);
        parts.push(<em key={`italic-${i}`}>{italicText}</em>);

        i = endItalic + 1;
        continue;
      }

      // Code with `
      if (text[i] === '`') {
        // Add current text if any
        if (currentText) {
          parts.push(<span key={`text-${i}`}>{currentText}</span>);
          currentText = '';
        }

        // Find closing `
        const startCode = i + 1;
        let endCode = text.indexOf('`', startCode);
        if (endCode === -1) endCode = text.length;

        const codeText = text.substring(startCode, endCode);
        parts.push(
          <code
            key={`code-${i}`}
            className="rounded bg-red-100 px-1 py-0.5 font-mono text-xs text-red-800 dark:bg-red-900/50 dark:text-red-300"
          >
            {codeText}
          </code>,
        );

        i = endCode + 1;
        continue;
      }

      // Regular text
      currentText += text[i];
      i++;
    }

    // Add any remaining text
    if (currentText) {
      parts.push(<span key="final-text">{currentText}</span>);
    }

    return parts.length > 0 ? <>{parts}</> : text;
  };

  // Prevent dialog from closing when clicking outside or pressing escape
  const handleOpenChange = (open: boolean) => {
    // Only allow closing through the button
    if (!open) {
      // Do nothing - don't allow closing
      return;
    }
  };

  // Debug information
  useEffect(() => {
    if (open) {
      console.group('DangerNotificationDialog Debug Info');

      // Check environment variables
      for (const key in import.meta.env) {
        if (key.startsWith('VITE_')) {
        }
      }
      console.groupEnd();
    }
  }, [open, notification, avatarUrl, avatarError, getUserInitials]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" hiddenCloseIcon={true}>
        {/* Big red alarm icon at the top */}
        <div className="absolute -top-12 left-1/2 flex h-24 w-24 -translate-x-1/2 items-center justify-center rounded-full bg-red-100 shadow-lg dark:bg-red-950">
          <AlertTriangle className="h-14 w-14 text-red-600 dark:text-red-400" />
        </div>

        <DialogHeader className="mt-10 flex flex-row items-center gap-4 border-b border-red-200 dark:border-red-900/40">
          <div className="flex-shrink-0">
            <Avatar className="h-12 w-12 border-2 border-red-200 dark:border-red-800">
              {avatarUrl && !avatarError ? (
                <AvatarImage
                  src={avatarUrl}
                  alt={notification.action_user_id?.empName || 'User'}
                  onError={handleAvatarError}
                />
              ) : (
                <AvatarFallback className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200">
                  {getUserInitials()}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex-1">
            <DialogTitle className="text-lg text-red-700 dark:text-red-400">
              {notification.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              {notification.action_user_id?.empName && (
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {notification.action_user_id.empName}
                </span>
              )}
              {notification.action_user_id?.empName && ' • '}
              {new Date(notification.createdAt).toLocaleString('ar-EG', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-md bg-red-50 p-4 dark:bg-red-950/30">
            {renderMarkdown()}
          </div>
        </div>

        <DialogFooter className="flex justify-end border-t border-red-200 dark:border-red-900/40">
          <Button
            onClick={handleMarkAsRead}
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
          >
            تم القراءة
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
